import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import WorkspacesRounded from '@mui/icons-material/WorkspacesRounded';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import NotificationDropdown from '@/components/layout/NotificationDropdown';
import { SHELL_BAR_HEIGHT } from '@/components/layout/SideNav';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeMode } from '@/contexts/ThemeContext';
import { paths } from '@/shared/routes/paths';
import { useRouteMeta } from '@/shared/routes/use-route-meta';

interface TopBarProps {
  sidenavWidth: number;
}

export default function TopBar({ sidenavWidth }: TopBarProps) {
  const { user, isOwner, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  // Same source as the browser-tab title; see RouteHandle.
  const { title: pageTitle, crumbs, titleInPage } = useRouteMeta();

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        // One surface for both zones. The admin bar used to paint its own dark
        // rgba, which could only ever describe a dark chrome.
        bgcolor: (t) => t.palette.surface.chrome,
        backdropFilter: 'blur(12px)',
        zIndex: (t) => t.zIndex.drawer + 1,
        ml: `${sidenavWidth}px`,
        width: `calc(100% - ${sidenavWidth}px)`,
        overflow: 'hidden',
        transition:
          'margin-left 0.2s ease-in-out, width 0.2s ease-in-out, background-color 0.45s ease, box-shadow 0.45s ease',
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          justifyContent: 'space-between',
          minHeight: SHELL_BAR_HEIGHT,
          height: SHELL_BAR_HEIGHT,
          // The rule belongs to the bar, not under it. On the AppBar it sat
          // outside an auto-height box and landed a pixel below the SideNav's,
          // which counts its own rule inside a fixed 52. Same number, two box
          // models, one visible step at the seam.
          borderBottom: (t) => `1px solid ${t.palette.surface.chromeBorder}`,
        }}
      >
        {/* Trail: left side. The ancestors answer "where am I", the title answers
            "what is this"; only the second one used to be here. */}
        {/* A top-level page with its own h1 still shows its name here, alone:
            every page has a trail, so the bar reads the same from one page to
            the next (#658). */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box
            component="nav"
            aria-label="Breadcrumb"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
          >
            {crumbs.map((crumb) => (
              // A crumb is a link only when its handle named a route. Most name a
              // section rather than a page ("Admin"), and a link to nowhere is
              // worse than no link; but where the ancestor IS a page, the trail
              // is the way back, and the side nav is not an answer when it is
              // collapsed to icons.
              <Box key={crumb.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="body2"
                  noWrap
                  {...(crumb.to ? { component: RouterLink, to: crumb.to } : {})}
                  sx={{
                    color: 'text.secondary',
                    ...(crumb.to
                      ? {
                          textDecoration: 'none',
                          '&:hover': { color: 'text.primary', textDecoration: 'underline' },
                        }
                      : {}),
                  }}
                >
                  {crumb.label}
                </Typography>
                <ChevronRightRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
              </Box>
            ))}
            {/* When the page carries its own h1 (`titleInPage`), the title stays in
                the trail but stops being the heading; otherwise the same words
                appear twice, fifty pixels apart. It is still emphasised, because
                the last crumb is where you are, not where you came from. */}
            <Typography
              variant={titleInPage ? 'body2' : 'h1'}
              component={titleInPage ? 'span' : 'h1'}
              noWrap
              sx={{
                fontWeight: titleInPage ? 600 : 700,
                color: 'text.primary',
                ...(titleInPage ? {} : { fontSize: '1.25rem' }),
              }}
            >
              {pageTitle}
            </Typography>
          </Box>
        </Box>

        {/*
          Actions, right side: two global controls, a rule, then who you are.

          Everything here is addressed to *you* rather than to the page, which
          is what the admin switch was not, and why it stayed at the foot of the
          nav. The rule separates controls from identity; it is not what stops
          this being a drawer. Three grouped items is.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationDropdown />

          {/*
            The theme toggle, out of the account menu and into the bar.

            Buried under an avatar it was two clicks and a guess: nothing about a
            person's initials says the light switch is behind them. It is also
            the one preference people change by time of day rather than once, so
            it earns a permanent target.

            The icon shows the mode you would GO to, not the one you are in: it
            is a button, and a button should picture its outcome. The tooltip
            says it in words, because an icon alone cannot disambiguate the two
            readings.
          */}
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              onClick={toggleMode}
              size="small"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={(t) => ({
                // Extra on the left only. The unread badge sits at the bell's
                // top-right and overhangs its button by about 4px, so the gap the
                // box model reports is not the gap the eye gets, and it is short
                // on this side alone.
                ml: 1,
                borderRadius: '8px',
                color: t.palette.text.secondary,
                '&:hover': { bgcolor: t.palette.action.hover, color: t.palette.text.primary },
              })}
            >
              {mode === 'dark' ? (
                <LightModeRounded fontSize="small" />
              ) : (
                <DarkModeRounded fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.25 }} />

          <Tooltip title={`${user?.first_name} ${user?.last_name}`}>
            <ButtonBase
              onClick={handleMenuOpen}
              id="user-menu-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              sx={(t) => ({
                borderRadius: '8px',
                p: 0.5,
                gap: 0.75,
                '&:hover': { bgcolor: t.palette.action.hover },
              })}
            >
              <Avatar
                // The admin's profile photo (#579); initials when there is none.
                src={user?.avatar_url ?? undefined}
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                }}
              >
                {user?.first_name?.charAt(0)}
                {user?.last_name?.charAt(0)}
              </Avatar>
              <ExpandMoreRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
            </ButtonBase>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                minWidth: 200,
                bgcolor: 'background.paper',
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
                boxShadow: (t) => `0 8px 24px ${alpha(t.palette.common.black, 0.3)}`,
              },
            },
          }}
        >
          {/* The name lives here rather than in the bar. It was a two-line
              name-and-role block wedged between the bell and the avatar, and it
              is the one thing in that group nobody clicks. */}
          <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {user?.role}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            component={RouterLink}
            to={paths.account}
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            <ListItemIcon>
              <PersonOutlineRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Account</ListItemText>
          </MenuItem>
          {isOwner && (
            <MenuItem
              component={RouterLink}
              to={paths.licence}
              sx={{ textDecoration: 'none', color: 'inherit' }}
            >
              <ListItemIcon>
                <WorkspacesRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText>Licence</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            component={RouterLink}
            to={paths.marketplace}
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            <ListItemIcon>
              <StorefrontRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Marketplace</ListItemText>
          </MenuItem>
          <MenuItem disabled>
            <ListItemIcon>
              <ReceiptLongRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Invoices</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={logout}>
            <ListItemIcon>
              <LogoutRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Log out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
